import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Listbox } from '@/components/ui/listbox';
import type { PopoverPortalTarget } from '@/components/ui/popover-portal';
import { api } from '@/lib/api';

interface Venue {
  id: string;
  name: string;
  isActive: boolean;
}

export interface VenueSelectProps {
  value: string | null;
  onChange: (venueId: string | null) => void;
  popoverPortal?: PopoverPortalTarget;
  disabled?: boolean;
  clearable?: boolean;
  id?: string;
  'aria-label'?: string;
}

/**
 * Picks a church venue defined in Settings. Empty list points admins to add venues.
 */
export const VenueSelect = ({
  value,
  onChange,
  popoverPortal = 'body',
  disabled = false,
  clearable = true,
  id,
  'aria-label': ariaLabel,
}: VenueSelectProps) => {
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation();

  const venues = useQuery({
    queryKey: ['venues', 'active'],
    queryFn: () =>
      api.get<{ data: Venue[] }>('/venues?pageSize=100&activeOnly=true'),
  });

  const options = useMemo(
    () =>
      (venues.data?.data ?? []).map((venue) => ({
        value: venue.id,
        label: venue.name,
      })),
    [venues.data?.data],
  );

  if (!venues.isLoading && options.length === 0) {
    return (
      <p className="text-body-sm text-fg-secondary">
        {t('venues.emptyPicker')}{' '}
        <Link to="/settings/venues" className="font-medium text-brand hover:underline">
          {t('venues.goToSettings')}
        </Link>
      </p>
    );
  }

  return (
    <Listbox
      id={id}
      value={value}
      onChange={onChange}
      options={options}
      placeholder={t('venues.placeholder')}
      clearable={clearable}
      clearLabel={tCommon('action.clearSelection')}
      disabled={disabled || venues.isPending}
      popoverPortal={popoverPortal}
      aria-label={ariaLabel ?? t('venues.placeholder')}
    />
  );
};
